import React, { useMemo } from 'react';
import styled from 'styled-components';
import { media } from '../../theme/tokens';
import { MetricCard } from '../../components';
import { usePrimeursPreparation } from 'apiTyped/hooks/primeurs.query';

export const PrimeursPreparationTab: React.FC = () => {
  const { isLoading, prepa, client_wines, rival_wines } =
    usePrimeursPreparation();

  const scatterData = prepa?.price_vs_score || [];
  const barData = prepa?.primeur_vs_web || [];
  const lineData = prepa?.all_notes || [];

  // Summary stats from scatter data
  const stats = useMemo(() => {
    if (!scatterData || scatterData.length === 0) return null;
    const prices = scatterData
      .map((d: any) => Number(d.price || 0))
      .filter(Boolean);
    const scores = scatterData
      .map((d: any) => Number(d.score || 0))
      .filter(Boolean);
    return {
      avgPrice:
        prices.length > 0
          ? (
              prices.reduce((a: number, b: number) => a + b, 0) / prices.length
            ).toFixed(0)
          : '—',
      avgScore:
        scores.length > 0
          ? (
              scores.reduce((a: number, b: number) => a + b, 0) / scores.length
            ).toFixed(1)
          : '—',
      wineCount: scatterData.length
    };
  }, [scatterData]);

  return (
    <Wrap>
      {/* Summary metrics */}
      <MetricsGrid>
        <MetricCard
          label="Wines analyzed"
          value={stats?.wineCount ?? '—'}
          unavailable={!stats}
        />
        <MetricCard
          label="Avg. price"
          value={stats?.avgPrice ?? '—'}
          unit="€"
          unavailable={!stats}
        />
        <MetricCard
          label="Avg. score"
          value={stats?.avgScore ?? '—'}
          unit="/100"
          unavailable={!stats}
        />
        <MetricCard
          label="Bar comparisons"
          value={barData.length > 0 ? barData.length.toString() : '—'}
          unavailable={barData.length === 0}
        />
      </MetricsGrid>

      {/* Price vs Score scatter */}
      <Section>
        <SectionTitle>Price vs. Score</SectionTitle>
        {scatterData.length > 0 ? (
          <ChartTable>
            <thead>
              <tr>
                <Th>Wine</Th>
                <Th>Vintage</Th>
                <Th align="right">Price (€)</Th>
                <Th align="right">Score</Th>
                <Th>Type</Th>
              </tr>
            </thead>
            <tbody>
              {scatterData.map((item: any, i: number) => {
                const isClient = client_wines.some(
                  (cw: any) => cw.wine === item.wine_name
                );
                return (
                  <tr key={i}>
                    <Td>
                      <WineName $client={isClient}>
                        {item.wine_name}
                        {isClient && <ClientBadge>yours</ClientBadge>}
                      </WineName>
                    </Td>
                    <Td>{item.vintage}</Td>
                    <Td align="right">
                      {item.price != null
                        ? `${Number(item.price).toFixed(0)}€`
                        : '—'}
                    </Td>
                    <Td align="right">
                      {item.score != null ? Number(item.score).toFixed(1) : '—'}
                    </Td>
                    <Td>{isClient ? 'Client' : 'Market'}</Td>
                  </tr>
                );
              })}
            </tbody>
          </ChartTable>
        ) : (
          <EmptyState>
            {isLoading ? 'Loading...' : 'No price vs. score data available'}
          </EmptyState>
        )}
      </Section>

      {/* Primeur vs Web bar comparison */}
      <Section>
        <SectionTitle>Primeur vs. Web Pricing</SectionTitle>
        {barData.length > 0 ? (
          <BarGrid>
            {barData.map((item: any, i: number) => (
              <BarCard key={i}>
                <BarWine>{item.wine_name}</BarWine>
                <BarRow>
                  <BarLabel>Primeur</BarLabel>
                  <Bar
                    $width={
                      item.primeur_price && item.web_price
                        ? (item.primeur_price /
                            Math.max(item.primeur_price, item.web_price)) *
                          100
                        : 50
                    }
                    $variant="primeur"
                  />
                  <BarValue>
                    {item.primeur_price != null
                      ? `${Number(item.primeur_price).toFixed(0)}€`
                      : '—'}
                  </BarValue>
                </BarRow>
                <BarRow>
                  <BarLabel>Web</BarLabel>
                  <Bar
                    $width={
                      item.web_price && item.primeur_price
                        ? (item.web_price /
                            Math.max(item.primeur_price, item.web_price)) *
                          100
                        : 50
                    }
                    $variant="web"
                  />
                  <BarValue>
                    {item.web_price != null
                      ? `${Number(item.web_price).toFixed(0)}€`
                      : '—'}
                  </BarValue>
                </BarRow>
                {item.primeur_price && item.web_price && (
                  <BarDiff $positive={item.primeur_price <= item.web_price}>
                    {item.primeur_price <= item.web_price ? '↓' : '↑'}{' '}
                    {Math.abs(
                      ((item.primeur_price - item.web_price) / item.web_price) *
                        100
                    ).toFixed(0)}
                    % vs web
                  </BarDiff>
                )}
              </BarCard>
            ))}
          </BarGrid>
        ) : (
          <EmptyState>
            {isLoading ? 'Loading...' : 'No primeur vs. web data available'}
          </EmptyState>
        )}
      </Section>

      {/* Notes history */}
      <Section>
        <SectionTitle>Notes History</SectionTitle>
        {lineData.length > 0 ? (
          <ChartTable>
            <thead>
              <tr>
                <Th>Wine</Th>
                <Th>Vintage</Th>
                <Th>Source</Th>
                <Th align="right">Score</Th>
                <Th>Date</Th>
              </tr>
            </thead>
            <tbody>
              {lineData.slice(0, 50).map((item: any, i: number) => (
                <tr key={i}>
                  <Td>{item.wine_name || '—'}</Td>
                  <Td>{item.vintage || '—'}</Td>
                  <Td>{item.source || '—'}</Td>
                  <Td align="right">
                    {item.score != null ? Number(item.score).toFixed(1) : '—'}
                  </Td>
                  <Td>{item.date || '—'}</Td>
                </tr>
              ))}
            </tbody>
          </ChartTable>
        ) : (
          <EmptyState>
            {isLoading ? 'Loading...' : 'No notes history available'}
          </EmptyState>
        )}
      </Section>
    </Wrap>
  );
};

// --- Styles ---

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  ${media.md} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Section = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  padding: 20px;
`;

const SectionTitle = styled.h3`
  font-family: var(--font-sans-bold);
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px;
  color: var(--ink);
`;

const ChartTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th<{ align?: string }>`
  text-align: ${({ align }) => align || 'left'};
  padding: 8px 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  border-bottom: 1px solid var(--line);
  font-weight: 600;
`;

const Td = styled.td<{ align?: string }>`
  text-align: ${({ align }) => align || 'left'};
  padding: 10px 12px;
  border-bottom: 1px solid var(--line-soft);
  color: var(--ink);
`;

const WineName = styled.span<{ $client: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: ${({ $client }) => ($client ? 600 : 400)};
`;

const ClientBadge = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: var(--radius-pill);
  background: var(--terracotta-soft);
  color: var(--terracotta);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`;

const EmptyState = styled.div`
  padding: 32px;
  text-align: center;
  color: var(--ink-faint);
  font-size: 13px;
`;

const BarGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  ${media.md} {
    grid-template-columns: 1fr 1fr;
  }
`;

const BarCard = styled.div`
  padding: 14px 16px;
  border: 1px solid var(--line-soft);
  border-radius: var(--radius-md);
`;

const BarWine = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 10px;
  color: var(--ink);
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const BarLabel = styled.span`
  font-size: 11px;
  width: 55px;
  color: var(--ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Bar = styled.div<{ $width: number; $variant: 'primeur' | 'web' }>`
  height: 8px;
  border-radius: 4px;
  width: ${({ $width }) => $width}%;
  max-width: 200px;
  background: ${({ $variant }) =>
    $variant === 'primeur' ? 'var(--terracotta)' : 'var(--ink-faint)'};
  transition: width 0.3s;
`;

const BarValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
`;

const BarDiff = styled.div<{ $positive: boolean }>`
  font-size: 11px;
  color: ${({ $positive }) =>
    $positive ? 'var(--olive)' : 'var(--terracotta)'};
  margin-top: 4px;
  font-weight: 500;
`;
